import React, { createContext, useContext, useState, useCallback } from 'react';

const MultiFormManagerContext = createContext();

export const MultiFormManagerProvider = ({ children }) => {
  const [registeredForms, setRegisteredForms] = useState({});

  const registerForm = useCallback((formName, formAPI) => {
    setRegisteredForms(prev => {
      return { 
        ...prev, 
        [formName]: formAPI 
      };
    });
  }, []);

  const unregisterForm = useCallback((formName) => {
    setRegisteredForms(prev => {
      const newForms = { ...prev };
      delete newForms[formName];
      return newForms;
    });
  }, []);

  const validateAllForms = useCallback(async () => {
    const validationResults = {};
    const formNames = Object.keys(registeredForms);
    
    for (const [formName, formAPI] of Object.entries(registeredForms)) {
      if (formAPI?.validate) {
        try {
          const result = await formAPI.validate();
          validationResults[formName] = result;
        } catch (error) {
          validationResults[formName] = { 
            isValid: false, 
            errors: { general: `Error de validación: ${error.message}` },
            values: {}
          };
        }
      } else {
      }
    }

    const allValid = Object.values(validationResults).every(result => result?.isValid);
    
    return { 
      results: validationResults, 
      allValid, 
      totalForms: formNames.length,
      validForms: Object.values(validationResults).filter(r => r?.isValid).length
    };
  }, [registeredForms]);

  const getAllFormValues = useCallback(() => {
    const allValues = {};
    
    for (const [formName, formAPI] of Object.entries(registeredForms)) {
      if (formAPI?.getValues) {
        allValues[formName] = formAPI.getValues();
      } else {
        allValues[formName] = {};
      }
    }
    
    return allValues;
  }, [registeredForms]);

  const getFormCount = useCallback(() => {
    return Object.keys(registeredForms).length;
  }, [registeredForms]);

  const getRegisteredFormNames = useCallback(() => {
    return Object.keys(registeredForms);
  }, [registeredForms]);

  const contextValue = {
    registerForm,
    unregisterForm,
    validateAllForms,
    getAllFormValues,
    getFormCount,
    getRegisteredFormNames,
    registeredForms
  };

  return (
    <MultiFormManagerContext.Provider value={contextValue}>
      {children}
    </MultiFormManagerContext.Provider>
  );
};

export const useMultiFormManager = () => {
  const context = useContext(MultiFormManagerContext);
  if (!context) {
    throw new Error('useMultiFormManager debe usarse dentro de MultiFormManagerProvider');
  }
  return context;
}; 